import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Plus, Edit01, Trash01, X } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { Tabs } from '@/components/application/tabs/tabs';
import { QuoteRuleForm } from '@/components/application/quotes/quote-rule-form';
import { PricingSettingsForm } from '@/components/application/quotes/pricing-settings-form';
import { EmailTemplateEditor } from '@/components/application/quotes/email-template-editor';
import {
    quoteRuleService,
    pricingSettingsService,
    emailTemplateService,
} from '@/data/mock-quotes';
import type {
    QuoteRule,
    PricingSettings,
    EmailTemplate,
    CreateQuoteRuleData,
    UpdateQuoteRuleData,
    CreatePricingSettingsData,
    UpdatePricingSettingsData,
    CreateEmailTemplateData,
    UpdateEmailTemplateData,
} from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';

export const ShipperQuotesSettingsPage = () => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('rules');
    
    const [rules, setRules] = useState<QuoteRule[]>([]);
    const [pricingSettings, setPricingSettings] = useState<PricingSettings[]>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    
    const [editingRule, setEditingRule] = useState<QuoteRule | undefined>();
    const [editingPricing, setEditingPricing] = useState<PricingSettings | undefined>();
    const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | undefined>();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        setRules(quoteRuleService.getAll());
        setPricingSettings(pricingSettingsService.getAll());
        setTemplates(emailTemplateService.getAll());
    };

    const handleCreateRule = () => {
        setEditingRule(undefined);
    };

    const handleEditRule = (rule: QuoteRule) => {
        setEditingRule(rule);
    };

    const handleDeleteRule = (id: string) => {
        if (confirm(t('settings.rules.deleteConfirm'))) {
            quoteRuleService.delete(id);
            loadData();
        }
    };

    const handleRuleSubmit = (data: CreateQuoteRuleData | UpdateQuoteRuleData) => {
        if (editingRule) {
            quoteRuleService.update(editingRule.id, data as UpdateQuoteRuleData);
        } else {
            quoteRuleService.create(data as CreateQuoteRuleData);
        }
        setEditingRule(undefined);
        loadData();
    };

    const handleRuleCancel = () => {
        setEditingRule(undefined);
    };

    const handleCreatePricing = () => {
        setEditingPricing(undefined);
    };

    const handleEditPricing = (settings: PricingSettings) => {
        setEditingPricing(settings);
    };

    const handleDeletePricing = (id: string) => {
        if (confirm(t('settings.pricing.deleteConfirm'))) {
            pricingSettingsService.delete(id);
            loadData();
        }
    };

    const handlePricingSubmit = (data: CreatePricingSettingsData | UpdatePricingSettingsData) => {
        if (editingPricing) {
            pricingSettingsService.update(editingPricing.id, data as UpdatePricingSettingsData);
        } else {
            pricingSettingsService.create(data as CreatePricingSettingsData);
        }
        setEditingPricing(undefined);
        loadData();
    };

    const handlePricingCancel = () => {
        setEditingPricing(undefined);
    };

    const handleCreateTemplate = () => {
        setEditingTemplate(undefined);
    };

    const handleEditTemplate = (template: EmailTemplate) => {
        setEditingTemplate(template);
    };

    const handleDeleteTemplate = (id: string) => {
        if (confirm(t('settings.templates.deleteConfirm'))) {
            emailTemplateService.delete(id);
            loadData();
        }
    };

    const handleTemplateSubmit = (data: CreateEmailTemplateData | UpdateEmailTemplateData) => {
        if (editingTemplate) {
            emailTemplateService.update(editingTemplate.id, data as UpdateEmailTemplateData);
        } else {
            emailTemplateService.create(data as CreateEmailTemplateData);
        }
        setEditingTemplate(undefined);
        loadData();
    };

    const handleTemplateCancel = () => {
        setEditingTemplate(undefined);
    };

    const isPanelOpen = editingRule !== undefined || editingPricing !== undefined || editingTemplate !== undefined;
    const getPanelTitle = () => {
        if (editingRule) return editingRule ? t('settings.rules.edit') : t('settings.rules.create');
        if (editingPricing) return editingPricing ? t('settings.pricing.edit') : t('settings.pricing.create');
        if (editingTemplate) return editingTemplate ? t('settings.templates.edit') : t('settings.templates.create');
        return '';
    };

    return (
        <div className="flex flex-row gap-0 -mx-4 -my-6 sm:-mx-6 lg:-mx-8">
            {/* Main Content - Right Side */}
            <div
                className={`flex flex-col transition-all duration-300 ${
                    isPanelOpen ? 'w-full lg:w-1/2 hidden lg:flex' : 'w-full'
                }`}
            >
                <div className="px-4 py-6 sm:px-6 lg:px-8 space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={() => navigate('/shipper/quotes')}
                        color="secondary"
                        size="md"
                        iconLeading={ArrowLeft}
                    >
                        {t('buttons.back', { ns: 'common' })}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('settings.title')}</h1>
                        <p className="text-tertiary mt-1">{t('settings.subtitle')}</p>
                    </div>
                </div>
                <ShipperOnboardingHeaderAction />
            </div>

            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as string)}>
                <Tabs.List items={[
                    { id: 'rules', label: t('settings.tabs.rules') },
                    { id: 'pricing', label: t('settings.tabs.pricing') },
                    { id: 'templates', label: t('settings.tabs.templates') },
                ]}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Rules Tab */}
                <Tabs.Panel id="rules">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-primary">{t('settings.rules.title')}</h2>
                                <p className="text-sm text-tertiary mt-1">{t('settings.rules.description')}</p>
                            </div>
                            <Button
                                onClick={handleCreateRule}
                                color="primary"
                                size="md"
                                iconLeading={Plus}
                            >
                                {t('settings.rules.create')}
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {rules.map((rule) => (
                                <div 
                                    key={rule.id} 
                                    className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary cursor-pointer hover:bg-primary_hover transition-colors ${
                                        editingRule?.id === rule.id ? 'bg-primary_hover' : ''
                                    }`}
                                    onClick={() => handleEditRule(rule)}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <h3 className={`font-semibold text-primary ${
                                                    isPanelOpen ? 'text-sm' : 'text-md'
                                                }`}>
                                                    {rule.name}
                                                </h3>
                                                <Badge color={rule.isActive ? 'success' : 'gray'} size="sm">
                                                    {rule.isActive ? t('settings.rules.active') : t('settings.rules.inactive')}
                                                </Badge>
                                            </div>
                                            {!isPanelOpen && (
                                                <>
                                                    {rule.description && (
                                                        <p className="text-sm text-tertiary mb-3 line-clamp-2">{rule.description}</p>
                                                    )}
                                                    <div className="space-y-2">
                                                        <div>
                                                            <div className="text-xs font-medium text-tertiary mb-1">
                                                                {t('settings.rules.naturalLanguageRule')}
                                                            </div>
                                                            <p className="text-sm text-primary line-clamp-2">{rule.naturalLanguageRule}</p>
                                                        </div>
                                                        {rule.autoResponseConditions.length > 0 && (
                                                            <div>
                                                                <div className="text-xs font-medium text-tertiary mb-1">
                                                                    {t('settings.rules.autoResponseConditions')}
                                                                </div>
                                                                <ul className="list-disc list-inside text-sm text-primary space-y-1">
                                                                    {rule.autoResponseConditions.slice(0, 2).map((condition, idx) => (
                                                                        <li key={idx}>{condition}</li>
                                                                    ))}
                                                                    {rule.autoResponseConditions.length > 2 && (
                                                                        <li className="text-tertiary">...</li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                onClick={() => handleEditRule(rule)}
                                                color="secondary"
                                                size="sm"
                                                iconLeading={Edit01}
                                            />
                                            <Button
                                                onClick={() => handleDeleteRule(rule.id)}
                                                color="primary-destructive"
                                                size="sm"
                                                iconLeading={Trash01}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {rules.length === 0 && (
                                <div className="rounded-xl bg-primary p-8 text-center shadow-xs ring-1 ring-secondary">
                                    <p className="text-tertiary">{t('settings.rules.noRules')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Tabs.Panel>

                {/* Pricing Tab */}
                <Tabs.Panel id="pricing">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-primary">{t('settings.pricing.title')}</h2>
                                <p className="text-sm text-tertiary mt-1">{t('settings.pricing.description')}</p>
                            </div>
                            <Button
                                onClick={handleCreatePricing}
                                color="primary"
                                size="md"
                                iconLeading={Plus}
                            >
                                {t('settings.pricing.create')}
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {pricingSettings.map((settings) => {
                                const rule = settings.ruleId ? rules.find(r => r.id === settings.ruleId) : null;
                                const marginDisplay = settings.margin.type === 'percentage'
                                    ? `${settings.margin.value}%`
                                    : `${settings.margin.value} ${settings.baseCurrency}`;
                                
                                return (
                                    <div 
                                        key={settings.id} 
                                        className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary cursor-pointer hover:bg-primary_hover transition-colors ${
                                            editingPricing?.id === settings.id ? 'bg-primary_hover' : ''
                                        }`}
                                        onClick={() => handleEditPricing(settings)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <h3 className={`font-semibold text-primary ${
                                                        isPanelOpen ? 'text-sm' : 'text-md'
                                                    }`}>
                                                        {rule ? rule.name : t('settings.pricing.default')}
                                                    </h3>
                                                    {rule && (
                                                        <Badge color="gray" size="sm">
                                                            {rule.name}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {!isPanelOpen && (
                                                    <>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                            <div>
                                                                <div className="text-xs font-medium text-tertiary mb-1">
                                                                    {t('settings.pricing.buyRate')}
                                                                </div>
                                                                <div className="text-sm text-primary">
                                                                    {settings.buyRate.toLocaleString('it-IT')} {settings.baseCurrency}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="text-xs font-medium text-tertiary mb-1">
                                                                    {t('settings.pricing.margin')}
                                                                </div>
                                                                <div className="text-sm text-primary">{marginDisplay}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-xs font-medium text-tertiary mb-1">
                                                                    {t('settings.pricing.components')}
                                                                </div>
                                                                <div className="text-sm text-primary">
                                                                    {settings.sellRateComponents.length} {t('settings.pricing.componentsCount')}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {settings.sellRateComponents.length > 0 && (
                                                            <div>
                                                                <div className="text-xs font-medium text-tertiary mb-2">
                                                                    {t('settings.pricing.sellRateComponents')}
                                                                </div>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {settings.sellRateComponents.slice(0, 3).map((comp) => (
                                                                        <span
                                                                            key={comp.id}
                                                                            className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-primary"
                                                                        >
                                                                            {comp.name}: {comp.type === 'percentage' ? `${comp.value}%` : `${comp.value} ${settings.baseCurrency}`}
                                                                        </span>
                                                                    ))}
                                                                    {settings.sellRateComponents.length > 3 && (
                                                                        <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                                            +{settings.sellRateComponents.length - 3} more
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    onClick={() => handleEditPricing(settings)}
                                                    color="secondary"
                                                    size="sm"
                                                    iconLeading={Edit01}
                                                />
                                                <Button
                                                    onClick={() => handleDeletePricing(settings.id)}
                                                    color="primary-destructive"
                                                    size="sm"
                                                    iconLeading={Trash01}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {pricingSettings.length === 0 && (
                                <div className="rounded-xl bg-primary p-8 text-center shadow-xs ring-1 ring-secondary">
                                    <p className="text-tertiary">{t('settings.pricing.noSettings')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Tabs.Panel>

                {/* Templates Tab */}
                <Tabs.Panel id="templates">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-primary">{t('settings.templates.title')}</h2>
                                <p className="text-sm text-tertiary mt-1">{t('settings.templates.description')}</p>
                            </div>
                            <Button
                                onClick={handleCreateTemplate}
                                color="primary"
                                size="md"
                                iconLeading={Plus}
                            >
                                {t('settings.templates.create')}
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {templates.map((template) => {
                                const rule = template.ruleId ? rules.find(r => r.id === template.ruleId) : null;
                                
                                return (
                                    <div 
                                        key={template.id} 
                                        className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary cursor-pointer hover:bg-primary_hover transition-colors ${
                                            editingTemplate?.id === template.id ? 'bg-primary_hover' : ''
                                        }`}
                                        onClick={() => handleEditTemplate(template)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                    <h3 className={`font-semibold text-primary ${
                                                        isPanelOpen ? 'text-sm' : 'text-md'
                                                    }`}>
                                                        {template.name}
                                                    </h3>
                                                    {rule && (
                                                        <Badge color="gray" size="sm">
                                                            {rule.name}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {!isPanelOpen && (
                                                    <div className="space-y-2 mb-4">
                                                        <div>
                                                            <div className="text-xs font-medium text-tertiary mb-1">
                                                                {t('settings.templates.subject')}
                                                            </div>
                                                            <p className="text-sm text-primary line-clamp-1">{template.subject}</p>
                                                        </div>
                                                        <div>
                                                            <div className="text-xs font-medium text-tertiary mb-1">
                                                                {t('settings.templates.body')}
                                                            </div>
                                                            <p className="text-sm text-primary line-clamp-2">{template.body}</p>
                                                        </div>
                                                        {template.draftingInstructions.length > 0 && (
                                                            <div>
                                                                <div className="text-xs font-medium text-tertiary mb-1">
                                                                    {t('settings.templates.draftingInstructions')}
                                                                </div>
                                                                <ul className="list-disc list-inside text-sm text-primary space-y-1">
                                                                    {template.draftingInstructions.slice(0, 2).map((inst, idx) => (
                                                                        <li key={inst.id || idx}>{inst.instruction}</li>
                                                                    ))}
                                                                    {template.draftingInstructions.length > 2 && (
                                                                        <li className="text-tertiary">...</li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    onClick={() => handleEditTemplate(template)}
                                                    color="secondary"
                                                    size="sm"
                                                    iconLeading={Edit01}
                                                />
                                                <Button
                                                    onClick={() => handleDeleteTemplate(template.id)}
                                                    color="primary-destructive"
                                                    size="sm"
                                                    iconLeading={Trash01}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {templates.length === 0 && (
                                <div className="rounded-xl bg-primary p-8 text-center shadow-xs ring-1 ring-secondary">
                                    <p className="text-tertiary">{t('settings.templates.noTemplates')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Tabs.Panel>
            </Tabs>
                </div>
            </div>

            {/* Detail Panel - Left Side */}
            {isPanelOpen && (
                <div className="flex-shrink-0 w-full lg:w-1/2 border-t lg:border-t-0 lg:border-r border-secondary order-first lg:order-last">
                    <div className="flex flex-col h-full bg-primary" style={{ minHeight: 'calc(100vh - 200px)' }}>
                        {/* Header */}
                        <div className="flex-shrink-0 px-6 py-4 border-b border-secondary">
                            <div className="flex items-center justify-between gap-4">
                                <h2 className="text-xl font-semibold text-primary">
                                    {getPanelTitle()}
                                </h2>
                                <Button
                                    onClick={() => {
                                        setEditingRule(undefined);
                                        setEditingPricing(undefined);
                                        setEditingTemplate(undefined);
                                    }}
                                    color="secondary"
                                    size="sm"
                                    iconLeading={X}
                                    aria-label="Close"
                                />
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            {editingRule !== undefined && (
                                <QuoteRuleForm
                                    rule={editingRule}
                                    onSubmit={handleRuleSubmit}
                                    onCancel={handleRuleCancel}
                                />
                            )}
                            {editingPricing !== undefined && (
                                <PricingSettingsForm
                                    settings={editingPricing}
                                    onSubmit={handlePricingSubmit}
                                    onCancel={handlePricingCancel}
                                />
                            )}
                            {editingTemplate !== undefined && (
                                <EmailTemplateEditor
                                    template={editingTemplate}
                                    onSubmit={handleTemplateSubmit}
                                    onCancel={handleTemplateCancel}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

